import { useLayoutEffect, useRef, useState } from "react";
import { XIcon } from "lucide-react";
import type { JSONContent } from "@tiptap/react";

import { CommentAuthorHeader } from "./CommentAuthorHeader";
import { InternalComment } from "./InternalComment";
import { InternalCommentComposer } from "./InternalCommentComposer";

import {
  Column,
  LoadingIndicator,
  RichTextDisplay,
  Row,
  Text,
} from "@/components";
import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";

import { useGetCandidacyStatusChanges } from "@/hooks/queries/useGetCandidacyStatusChanges";
import { useGetInternalCandidacyComments } from "@/hooks/queries/useGetInternalCandidacyComments";
import { useGetUser } from "@/hooks/queries/useGetUser";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { formatting } from "@/lib/formatting";
import {
  CandidacyStatus,
  type CandidacyStatusChange,
  type InternalCandidacyComment as InternalCandidacyCommentType,
  type InternalCommentActivity,
} from "@/types";

// A rejection with its reason reads like a comment in the feed, but can't be
// edited or removed.
const RejectionStatusRow = ({
  statusChange,
}: {
  statusChange: CandidacyStatusChange;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: author, status: authorStatus } = useGetUser({
    userId: statusChange.userId,
  });

  /* Helpers */
  const authorName =
    authorStatus === "success"
      ? `${author.firstName} ${author.lastName}`
      : "Staffer";

  /* Render */
  const render = () => {
    return (
      <Column className="gap-1.5">
        <CommentAuthorHeader
          createdAt={statusChange.createdAt}
          icon={
            <Row className="h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500 text-white">
              <XIcon className="h-4 w-4" />
            </Row>
          }
          name={authorName}
        />
        <div className="pl-8 text-sm font-normal leading-snug text-slate-600">
          {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
          <RichTextDisplay richText={statusChange.statusChangeReason || ""} />
        </div>
      </Column>
    );
  };

  return render();
};

export const InternalComments = ({
  commentDraft,
  onCommentDraftChange,
}: {
  commentDraft: JSONContent;
  onCommentDraftChange: (contentJson: JSONContent) => void;
}) => {
  /* Context */
  const { candidacyId } = useSheetScope();
  const { jobId, workerId } = useUserSheet();

  /* Database CRUD Operations */
  // Read
  const userApi = useGetCandidacySheetData({ jobId, userId: workerId });
  const commentsQuery = useGetInternalCandidacyComments({ candidacyId });
  const statusChangesQuery = useGetCandidacyStatusChanges({ candidacyId });

  /* React Hooks */
  // Only one thread's reply composer can be open at a time.
  const [replyingCommentId, setReplyingCommentId] = useState<number | null>(
    null,
  );
  const feedRef = useRef<HTMLDivElement>(null);
  const hasScrolledToLatestComment = useRef(false);
  const commentCount = commentsQuery.data?.length ?? 0;
  const statusChangeCount = statusChangesQuery.data?.length ?? 0;

  useLayoutEffect(() => {
    if (
      hasScrolledToLatestComment.current ||
      (commentCount === 0 && statusChangeCount === 0)
    ) {
      return;
    }

    const feedElement = feedRef.current;
    if (!feedElement) return;

    feedElement.scrollTop = feedElement.scrollHeight;
    hasScrolledToLatestComment.current = true;
  }, [commentCount, statusChangeCount]);

  /* Helpers */
  const candidateName = formatting.fullName(
    userApi.data?.profile.firstName,
    userApi.data?.profile.lastName,
    "the candidate",
  );

  // Replies render nested under their thread's first comment, oldest first.
  const buildRepliesByThreadId = () => {
    const repliesByThreadId = new Map<number, InternalCandidacyCommentType[]>();

    for (const comment of commentsQuery.data ?? []) {
      if (comment.threadId === null) continue;

      const threadReplies = repliesByThreadId.get(comment.threadId) ?? [];
      threadReplies.push(comment);
      repliesByThreadId.set(comment.threadId, threadReplies);
    }
    for (const threadReplies of repliesByThreadId.values()) {
      threadReplies.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
    }

    return repliesByThreadId;
  };

  // Rejection reasons are optional enrichment — don't block the comment list
  // while that query is pending or failed.
  const buildFeed = (): InternalCommentActivity[] => {
    const feed: InternalCommentActivity[] = (commentsQuery.data ?? [])
      .filter((comment) => comment.threadId === null)
      .map((internalCandidacyComment) => ({
        type: "internalCandidacyComment" as const,
        internalCandidacyComment,
        createdAt: internalCandidacyComment.createdAt,
      }));

    for (const statusChange of statusChangesQuery.data ?? []) {
      if (
        statusChange.toStatus !== CandidacyStatus.REJECTED ||
        !statusChange.statusChangeReason
      ) {
        continue;
      }

      feed.push({
        type: "candidacyStatusChange" as const,
        candidacyStatusChange: statusChange,
        createdAt: statusChange.createdAt,
      });
    }

    // Oldest first — newest comment lands at the bottom, next to the composer.
    return feed.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  };

  /* Render */
  const renderFeed = () => {
    if (commentsQuery.isPending) {
      return <LoadingIndicator />;
    }

    if (commentsQuery.isError) {
      return (
        <Text className="text-sm font-normal text-slate-500">
          Couldn&apos;t load comments. Try again.
        </Text>
      );
    }

    const feed = buildFeed();

    if (feed.length === 0) {
      return (
        <Text className="text-sm font-normal text-slate-500">
          No comments yet.
        </Text>
      );
    }

    const repliesByThreadId = buildRepliesByThreadId();

    return (
      <Column className="gap-5">
        {feed.map((entry) => {
          if (entry.type === "internalCandidacyComment") {
            return (
              <InternalComment
                comment={entry.internalCandidacyComment}
                key={entry.internalCandidacyComment.id}
                onToggleReplying={setReplyingCommentId}
                replies={
                  repliesByThreadId.get(entry.internalCandidacyComment.id) ?? []
                }
                replyingCommentId={replyingCommentId}
              />
            );
          }

          return (
            <RejectionStatusRow
              key={`status-${String(entry.candidacyStatusChange.id)}`}
              statusChange={entry.candidacyStatusChange}
            />
          );
        })}
      </Column>
    );
  };

  const render = () => {
    return (
      <Column className="h-full min-h-0 w-full gap-0">
        <Column className="min-h-0 flex-1 overflow-y-auto pb-3" ref={feedRef}>
          {renderFeed()}
        </Column>
        <Column className="shrink-0">
          <InternalCommentComposer
            candidacyId={candidacyId}
            candidateName={candidateName}
            commentDraft={commentDraft}
            onCommentDraftChange={onCommentDraftChange}
          />
        </Column>
      </Column>
    );
  };

  return render();
};
